/**
 * Product Breadcrumb Handler
 * Updates the product page breadcrumb based on the referrer (the page the user came from)
 */

class ProductBreadcrumb {
  constructor() {
    this.init();
  }

  getSessionItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  setSessionItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      // Ignore storage errors in restricted environments
    }
  }

  removeSessionItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      // Ignore storage errors in restricted environments
    }
  }

  /**
   * Normalize a string for comparison by removing special characters, accents, and apostrophes
   * Examples:
   * - "arc'teryx" -> "arcteryx"
   * - "Arcteryx" -> "arcteryx"
   * - "Arc'teryx" -> "arcteryx"
   */
  normalizeForComparison(str) {
    return str
      .toLowerCase()
      .normalize('NFD') // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/['\u2019\u2018]/g, '') // Remove apostrophes (regular and smart quotes)
      .replace(/[^a-z0-9]/g, ''); // Remove all non-alphanumeric characters
  }

  init() {
    // Get the referrer URL
    const referrer = document.referrer;

    // Get breadcrumb elements
    const breadcrumbWrapper = document.querySelector('.product-title-bar .collection-hero__title-wrapper');

    if (!breadcrumbWrapper) return;

    // Parse the referrer to extract collection information
    const collectionInfo = this.parseReferrer(referrer);

    // Update the breadcrumb based on the collection info
    this.updateBreadcrumb(breadcrumbWrapper, collectionInfo);
  }

  parseReferrer(referrer) {
    // Check if we previously came from brands page (stored in session)
    const cameFromBrandsSession = this.getSessionItem('cameFromBrands') === 'true';
    const configuredBrandsPageUrl = window.productBreadcrumbConfig?.brandsPageUrl || '/pages/brands';
    let brandsPageUrl = this.getSessionItem('brandsPageUrl') || configuredBrandsPageUrl;

    if (!referrer) {
      return { hasCollection: false, cameFromBrands: cameFromBrandsSession, brandsPageUrl: brandsPageUrl };
    }

    try {
      const url = new URL(referrer);
      const isSameOrigin = url.origin === window.location.origin;
      const isBrandsPageReferrer = /^(?:\/[a-z]{2})?\/pages\/(brands|marques)\/?$/.test(url.pathname);
      const normalizedBrandsPath = url.pathname.replace(/^\/fr(?=\/)/, '');

      if (isSameOrigin && isBrandsPageReferrer) {
        this.setSessionItem('cameFromBrands', 'true');
        this.setSessionItem('brandsPageUrl', normalizedBrandsPath);
        brandsPageUrl = normalizedBrandsPath;
      } else if (isSameOrigin && !isBrandsPageReferrer) {
        // Prevent stale state from older journeys affecting current breadcrumb
        this.removeSessionItem('cameFromBrands');
      }

      const cameFromBrands = isSameOrigin && isBrandsPageReferrer ? true : cameFromBrandsSession;

      // Check if the referrer is from a collection page
      const pathMatch = url.pathname.match(/\/collections\/([^\/\?]+)/);

      if (pathMatch) {
        const collectionHandle = pathMatch[1];

        // Get the collection title
        let collectionTitle;

        // Use translation for "all" collection, otherwise check for category translations
        if (collectionHandle === 'all') {
          collectionTitle = window.productBreadcrumbTranslations?.all || 'All';
        } else {
          // Check if there's a translation available for this collection category
          const categoryTranslations = window.productBreadcrumbTranslations?.categories || {};
          if (categoryTranslations[collectionHandle]) {
            collectionTitle = categoryTranslations[collectionHandle];
          } else {
            // Fallback to formatting the handle
            collectionTitle = collectionHandle.replace(/-/g, ' ');
            // Capitalize first letter of each word
            collectionTitle = collectionTitle
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
        }

        return {
          hasCollection: true,
          handle: collectionHandle,
          title: collectionTitle,
          url: `${url.pathname}${url.search}`,
          cameFromBrands,
          brandsPageUrl: brandsPageUrl
        };
      }
    } catch (e) {
      // Malformed referrer URL; ignore.
    }

    return { hasCollection: false, cameFromBrands: cameFromBrandsSession, brandsPageUrl: brandsPageUrl };
  }

  updateBreadcrumb(wrapper, collectionInfo) {
    // Get current breadcrumb parts
    const titleElement = wrapper.querySelector('h1.collection-hero__title');
    const separators = wrapper.querySelectorAll('.collection-hero__title-seperator');
    const firstSeparator = separators[0]; // First separator after h1
    const secondSeparator = separators[1]; // Second separator after vendor
    const vendorBreadcrumb = wrapper.querySelector('.collection-hero__title-breadcrumb');

    if (!titleElement || !firstSeparator || !vendorBreadcrumb) {
      return;
    }

    // Track whether we changed content so we can recalc the title-bar margin
    let contentChanged = false;

    if (collectionInfo.hasCollection) {
      // Get the current vendor/collection name from the breadcrumb
      const vendorName = vendorBreadcrumb.textContent.trim();
      const collectionName = collectionInfo.title;

      // Normalize both names for comparison (removes special chars, apostrophes, etc.)
      const normalizedVendor = this.normalizeForComparison(vendorName);
      const normalizedCollection = this.normalizeForComparison(collectionName);

      // Check if the collection is a vendor collection.
      // Some brands have collection aliases (e.g. "Hoka" collection, "Hoka One One" vendor),
      // so we support exact and containment matches.
      const normalizedHandle = this.normalizeForComparison(collectionInfo.handle || '');
      const isVendorCollection =
        normalizedVendor === normalizedCollection
        || normalizedVendor === normalizedHandle
        || normalizedVendor.includes(normalizedCollection)
        || normalizedCollection.includes(normalizedVendor)
        || normalizedVendor.includes(normalizedHandle)
        || normalizedHandle.includes(normalizedVendor);

      if (isVendorCollection && collectionInfo.cameFromBrands) {
        // Show "Brands / Vendor / Product" - user came through brands page
        // First breadcrumb shows "Brands", vendor breadcrumb shows the actual brand
        const brandsText = window.productBreadcrumbTranslations?.brands || 'Brands';
        const collectionLabel = window.productBreadcrumbTranslations?.brands || 'Brands';
        const brandsUrl = collectionInfo.brandsPageUrl || '/pages/brands';
        titleElement.innerHTML = `<span class="visually-hidden">${collectionLabel}: </span><a href="${brandsUrl}">${brandsText}</a>`;

        // Show all elements: title, first separator, vendor, second separator
        titleElement.style.display = 'block';
        firstSeparator.style.display = 'inline';
        vendorBreadcrumb.style.display = 'inline';
        if (secondSeparator) {
          secondSeparator.style.display = 'inline';
        }
        contentChanged = true;
      } else if (isVendorCollection) {
        // Collection matches vendor but didn't come from brands page
        // Show "Vendor / Product" by hiding the first breadcrumb and its separator
        titleElement.style.display = 'none';
        firstSeparator.style.display = 'none';
        vendorBreadcrumb.style.display = 'inline';
        if (secondSeparator) {
          secondSeparator.style.display = 'inline';
        }
        contentChanged = true;
      } else {
        // Show the collection normally for non-vendor collections
        const collectionLabel = window.productBreadcrumbTranslations?.collection || 'Collection';
        titleElement.innerHTML = `<span class="visually-hidden">${collectionLabel}: </span><a href="${collectionInfo.url}">${collectionInfo.title}</a>`;
        titleElement.style.display = 'block';
        firstSeparator.style.display = 'inline';
        vendorBreadcrumb.style.display = 'inline';
        if (secondSeparator) {
          secondSeparator.style.display = 'inline';
        }
        contentChanged = true;
      }
    } else {
      // User came from external source - hide the collection part
      titleElement.style.display = 'none';
      firstSeparator.style.display = 'none';
      // Make sure vendor breadcrumb and separator are visible when no collection
      vendorBreadcrumb.style.display = '';
      if (secondSeparator) {
        secondSeparator.style.display = '';
      }
      // Clear the session storage when coming from external source
      this.removeSessionItem('cameFromBrands');
      contentChanged = true;
    }

    // The inline script in theme.liquid calculates a negative margin-bottom on
    // .product-title-bar to collapse the breadcrumb's space.  That measurement
    // happens before this deferred script runs, so if we just showed/hid the h1
    // the wrapper height changed and the margin is stale → whitespace gap.
    if (contentChanged) {
      this.recalcTitleBarMargin(wrapper);
    }
  }

  /**
   * Recalculate the negative margin-bottom on .product-title-bar so it
   * matches the current wrapper height (which may have changed after we
   * showed / hid breadcrumb elements).
   */
  recalcTitleBarMargin(wrapper) {
    const bar = document.querySelector('.product-title-bar');
    if (!bar || !wrapper) return;

    const mt = parseFloat(getComputedStyle(wrapper).marginTop) || 0;
    bar.style.marginBottom = '-' + (mt + wrapper.offsetHeight) + 'px';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductBreadcrumb();
  });
} else {
  new ProductBreadcrumb();
}
